import { csrfToken } from "@/lib/csrf-token"

export type ApiResult<T> =
  | { ok: true; data: T; status: number }
  | { ok: false; data: null; errors: string[]; status: number }

const NETWORK_ERROR = "Network error. Please try again."

export function getJson<T>(url: string): Promise<ApiResult<T>> {
  return jsonRequest("GET", url)
}

export function postJson<T>(
  url: string,
  body?: unknown,
): Promise<ApiResult<T>> {
  return jsonRequest("POST", url, body)
}

export function patchJson<T>(
  url: string,
  body: unknown,
): Promise<ApiResult<T>> {
  return jsonRequest("PATCH", url, body)
}

export function deleteJson<T>(url: string): Promise<ApiResult<T>> {
  return jsonRequest("DELETE", url)
}

export function postForm<T>(
  url: string,
  formData: FormData,
): Promise<ApiResult<T>> {
  return jsonRequest("POST", url, formData)
}

// Shared boilerplate for the helpers above: Accept/Content-Type headers, the CSRF
// token on mutating requests, body serialization, and error extraction. Returns a
// discriminated result so callers branch on `result.ok` (no try/catch). A
// `FormData` body is sent as-is — the browser sets the multipart Content-Type with
// its boundary; any other body is serialized as JSON.
async function jsonRequest<T>(
  method: string,
  url: string,
  body?: unknown,
): Promise<ApiResult<T>> {
  const isFormData = body instanceof FormData
  const headers: Record<string, string> = { Accept: "application/json" }
  if (method !== "GET" && method !== "HEAD") {
    headers["X-CSRF-Token"] = csrfToken()
  }
  if (body !== undefined && !isFormData) {
    headers["Content-Type"] = "application/json"
  }

  try {
    const response = await fetch(url, {
      method,
      headers,
      body:
        body === undefined
          ? undefined
          : isFormData
            ? body
            : JSON.stringify(body),
    })

    if (!response.ok) {
      return {
        ok: false,
        data: null,
        errors: await errorsFromResponse(response),
        status: response.status,
      }
    }

    const data = (await response.json().catch(() => null)) as T
    return { ok: true, data, status: response.status }
  } catch {
    return { ok: false, data: null, errors: [NETWORK_ERROR], status: 0 }
  }
}

async function errorsFromResponse(response: Response): Promise<string[]> {
  const payload = (await response.json().catch(() => null)) as {
    errors?: string[]
  } | null
  return payload?.errors ?? []
}
